import React from 'react';
import { AlertTriangle, Clock, RotateCcw, X } from 'lucide-react';

const DataRecoveryModal = ({ 
  isOpen, 
  recoveryData, 
  onAccept, 
  onDecline, 
  title = "Unsaved Changes Found",
  description = "We found unsaved changes from your previous session. Would you like to restore them?"
}) => {
  if (!isOpen) return null;

  const formatTimestamp = (timestamp) => {
    try {
      const date = new Date(timestamp);
      const now = new Date();
      const diff = now - date;
      const minutes = Math.floor(diff / (1000 * 60));
      const hours = Math.floor(diff / (1000 * 60 * 60));
      
      if (minutes < 60) {
        return `${minutes} minute${minutes !== 1 ? 's' : ''} ago`;
      } else if (hours < 24) {
        return `${hours} hour${hours !== 1 ? 's' : ''} ago`;
      } else {
        return date.toLocaleDateString() + ' at ' + date.toLocaleTimeString();
      }
    } catch (error) {
      return 'recently';
    }
  };

  const getDataSummary = (data) => {
    if (!data || typeof data !== 'object') return 'Some form data';
    
    const keys = Object.keys(data);
    const nonEmptyKeys = keys.filter(key => {
      const value = data[key];
      return value && value !== '' && !(Array.isArray(value) && value.length === 0);
    });
    
    if (nonEmptyKeys.length === 0) return 'Some form data';
    
    // Create a helpful summary based on common field names
    const summaryParts = [];
    
    if (data.name || data.title) {
      summaryParts.push(`"${data.name || data.title}"`);
    }
    
    if (data.responses && Object.keys(data.responses).length > 0) {
      summaryParts.push(`${Object.keys(data.responses).length} assessment responses`);
    }
    
    if (data.essays && data.essays.length > 0) {
      summaryParts.push(`${data.essays.length} essay${data.essays.length !== 1 ? 's' : ''}`);
    }
    
    if (data.colleges && data.colleges.length > 0) {
      summaryParts.push(`${data.colleges.length} college${data.colleges.length !== 1 ? 's' : ''}`);
    }
    
    if (summaryParts.length > 0) {
      return summaryParts.join(', ');
    }
    
    return `${nonEmptyKeys.length} field${nonEmptyKeys.length !== 1 ? 's' : ''} completed`;
  };

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full mx-4 overflow-hidden">
        {/* Header */}
        <div className="bg-gradient-to-r from-amber-50 to-orange-50 p-6 border-b border-amber-100">
          <div className="flex items-start gap-4">
            <div className="p-2 bg-amber-100 rounded-xl">
              <AlertTriangle className="w-6 h-6 text-amber-600" />
            </div>
            <div className="flex-1">
              <h2 className="text-xl font-bold text-slate-900 mb-1">
                {title}
              </h2>
              <p className="text-slate-600 text-sm">
                {description}
              </p>
            </div>
          </div>
        </div>

        {/* Content */}
        <div className="p-6">
          {recoveryData && (
            <div className="bg-stone-50 rounded-xl p-4 mb-6">
              <div className="flex items-center gap-3 mb-3">
                <Clock className="w-4 h-4 text-stone-500" />
                <span className="text-sm font-medium text-slate-700">
                  Last saved {formatTimestamp(recoveryData.timestamp)}
                </span>
              </div>
              
              <div className="text-sm text-slate-600">
                <strong>Recovered data:</strong> {getDataSummary(recoveryData.data)}
              </div>
            </div>
          )}

          <div className="flex flex-col sm:flex-row gap-3">
            <button
              onClick={onAccept}
              className="flex-1 flex items-center justify-center gap-2 bg-teal-600 text-white px-4 py-3 rounded-xl font-medium hover:bg-teal-700 transition-colors"
            >
              <RotateCcw className="w-4 h-4" />
              Restore Data
            </button>
            
            <button
              onClick={onDecline}
              className="flex-1 flex items-center justify-center gap-2 bg-stone-100 text-slate-700 px-4 py-3 rounded-xl font-medium hover:bg-stone-200 transition-colors"
            >
              <X className="w-4 h-4" />
              Start Fresh
            </button>
          </div>
          
          <p className="text-xs text-stone-500 mt-4 text-center">
            This data was automatically saved to prevent loss during your previous session.
          </p>
        </div>
      </div>
    </div>
  );
};

export default DataRecoveryModal;